// ---------------------------------------------------------------------------
// Icon / badge resolvers for the Access Legitimacy grid (design v6 §3.3).
//
// The wire sends OPEN lowercase per-tenant taxonomy labels; `@dawn/ui`'s icon
// atoms key on CLOSED literal-union vocabularies and crash on an unknown name
// (`FileTypeIcon` / `ConnectedServiceIcon` have no fallback today). These pure
// resolvers bridge label → icon vocabulary, each with a defensive fallback so an
// unrecognized label never crashes or renders broken (PRD story 25):
//
//   - file-type / location → the icon id, or `null` (caller renders a generic
//     fallback glyph).
//   - user-type → a badge descriptor, ALWAYS (unknown → a neutral HI badge,
//     losing only the human/non-human distinction — never crashing).
//   - action    → the action-history icon id; every action in the closed server
//     vocabulary has its own glyph, with `'other'` as the defensive fallback for
//     any label outside that set so it takes a glyph rather than being dropped.
//
// Keyed on the interim global taxonomy's seeded LABEL forms
// (`data-access/postgres/seed/taxonomy-vocabulary` + the mock pools), per
// ADR 0003. Re-declared here as plain string keys — `packages/ui` must not
// import `@dawn/feature-config` (agnosticism firewall).
// ---------------------------------------------------------------------------

import type { BrandIconId } from '../assets/brandIcons';
import type { FileTypeIconName } from '../icons';

/**
 * Action-history icon id — one per action in the closed server vocabulary
 * (`view|download|edit|share|delete|print|copy`, mapped to these seven ids),
 * plus `'other'` as the defensive fallback for any label outside that set. Owned
 * here so the resolver and `EventActionHistoryTable` share one source of truth.
 */
export type EventActionIconId =
  | 'read'
  | 'download'
  | 'edit'
  | 'share'
  | 'delete'
  | 'print'
  | 'copy'
  | 'other';

/** Badge descriptor consumed by `UserTypeBadge` (type discriminator + external marker). */
export interface UserTypeBadgeDescriptor {
  type: 'HI' | 'NHI';
  isExternal: boolean;
}

// File-type label → `FileTypeIcon` glyph name. Seed labels diverge from the
// icon vocabulary for three values (`presentation`/`video`/`audio`), so they
// are remapped; the rest are 1:1.
const FILE_TYPE_ICON_BY_LABEL: Readonly<Record<string, FileTypeIconName>> = {
  zip: 'zip',
  html: 'html',
  png: 'png',
  jpeg: 'jpeg',
  spreadsheet: 'spreadsheet',
  presentation: 'ppt',
  video: 'mov',
  audio: 'mp3',
  pdf: 'pdf',
};

// Location label → connected-service brand icon. Several mock locations have no
// brand glyph (`notion`, `confluence`, `jira`, `box`) and resolve to `null`.
const LOCATION_ICON_BY_LABEL: Readonly<Record<string, BrandIconId>> = {
  'google drive': 'google-workspace',
  'google docs': 'google-workspace',
  'google sheets': 'google-workspace',
  onedrive: 'onedrive',
  sharepoint: 'sharepoint',
  outlook: 'office365',
  slack: 'slack',
  github: 'github',
  'amazon s3': 's3',
  dropbox: 'dropbox',
};

// User-type label → badge descriptor. The interim taxonomy stores label forms
// (`internal human` / `external human` / `non-human identity`, per ADR 0003 —
// NOT the glossary slugs). `external human` is the only externally-marked human.
const USER_TYPE_BADGE_BY_LABEL: Readonly<Record<string, UserTypeBadgeDescriptor>> = {
  'internal human': { type: 'HI', isExternal: false },
  'external human': { type: 'HI', isExternal: true },
  'non-human identity': { type: 'NHI', isExternal: false },
};

// Wire action label → action-history icon id. The closed server vocabulary is
// `view|download|edit|share|delete|print|copy`; each maps to its own glyph
// (`view` → the `read` book glyph), so every action is visually distinct.
const ACTION_ICON_BY_LABEL: Readonly<Record<string, EventActionIconId>> = {
  view: 'read',
  download: 'download',
  edit: 'edit',
  share: 'share',
  delete: 'delete',
  print: 'print',
  copy: 'copy',
};

/** Neutral fallback badge for an unrecognized user-type label. */
const NEUTRAL_USER_TYPE_BADGE: UserTypeBadgeDescriptor = { type: 'HI', isExternal: false };

/** Resolves a file-type label to its glyph name, or `null` when unrecognized. */
export function resolveFileTypeIcon(label: string | null | undefined): FileTypeIconName | null {
  if (label == null) return null;
  return FILE_TYPE_ICON_BY_LABEL[label] ?? null;
}

/** Resolves a location label to its brand icon id, or `null` when unrecognized. */
export function resolveLocationIcon(label: string | null | undefined): BrandIconId | null {
  if (label == null) return null;
  return LOCATION_ICON_BY_LABEL[label] ?? null;
}

/**
 * Resolves a user-type label to a `UserTypeBadge` descriptor. Always returns a
 * descriptor — an unrecognized label falls back to a neutral internal-human
 * badge (degrades gracefully, losing only the human/non-human distinction).
 */
export function resolveUserTypeBadge(label: string | null | undefined): UserTypeBadgeDescriptor {
  if (label == null) return NEUTRAL_USER_TYPE_BADGE;
  return USER_TYPE_BADGE_BY_LABEL[label] ?? NEUTRAL_USER_TYPE_BADGE;
}

/**
 * Resolves a wire action label to an action-history icon id. Every action in the
 * closed server vocabulary maps to its own glyph; a label outside that set (or
 * `null`) resolves to `'other'` so it renders the fallback glyph rather than
 * being dropped.
 */
export function resolveActionIcon(label: string | null | undefined): EventActionIconId {
  if (label == null) return 'other';
  return ACTION_ICON_BY_LABEL[label] ?? 'other';
}
